import { OnboardingStep } from '@reelplicate/api-client/model';
import { useGetMe } from '@reelplicate/api-client/users';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { pathForProgress, requiredProgress, stepIndex } from './steps';

function Loading() {
  return <div aria-busy="true" className="min-h-svh" />;
}

/**
 * Wraps the app shell (inside RequireAuth): until someone has finished
 * onboarding, every app route sends them to the step they're on - after
 * sign-up that's the first one, later it's wherever they left off.
 */
export function RequireOnboarded({ children }: { children?: ReactNode }) {
  const { data: me, error } = useGetMe();
  if (error) throw error;
  if (!me) return <Loading />;
  if (me.onboardingStep !== OnboardingStep.COMPLETED) {
    return <Navigate to={pathForProgress(me.onboardingStep)} replace />;
  }
  return children ?? <Outlet />;
}

/**
 * Wraps the onboarding screens: they can revisit earlier steps but not skip
 * ahead of their saved progress, and once onboarding is done only the
 * "Setting up" and "All set" screens stay open - the rest go to the app.
 */
export function OnboardingGate({ children }: { children?: ReactNode }) {
  const { data: me, error } = useGetMe();
  const { pathname } = useLocation();
  if (error) throw error;
  if (!me) return <Loading />;

  const needed = requiredProgress(pathname);
  const saved = me.onboardingStep;
  if (saved === OnboardingStep.COMPLETED && needed !== OnboardingStep.COMPLETED) {
    return <Navigate to="/" replace />;
  }
  if (stepIndex(needed) > stepIndex(saved)) {
    return <Navigate to={pathForProgress(saved)} replace />;
  }
  return children ?? <Outlet />;
}
